'use client';

import { m, LazyMotion, domAnimation, useInView } from 'framer-motion';
import { useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { CardContent, CardMedia } from '@mui/material';

import { CONFIG } from 'src/global-config';
import { Image } from 'src/components/image';
import { useLocale } from 'src/locales';

export function TransparencyRegulatoryAssurance({ sx, ...other }) {
  const lang = localStorage.getItem('i18nextLng') || 'ar';

  const { t } = useLocale();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const VISIONS = [
    {
      image: `${CONFIG.assetsDir}/assets/images/home/assurance/shahen-app-shipment-tracking-booking.webp`,
      name: 'Comprehensive_Trip_Documentation',
      description: 'Comprehensive_Trip_Documentation_Description',
      alt: t('Shahen_app_for_booking_trucks_and_shipment_tracking_for_businesses'),
    },
    {
      image: `${CONFIG.assetsDir}/assets/images/home/assurance/saudi-domestic-road-freight-riyadh-jeddah.webp`,
      name: 'Insured_Cargo_Protection',
      description: 'Insured_Cargo_Protection_Description',
      alt: t('Road_freight_services_from_Riyadh_to_Jeddah_Dammam_and_all_KSA_cities'),
    },
    {
      image: `${CONFIG.assetsDir}/assets/images/home/assurance/dyna-truck-for-rent-saudi-arabia.webp`,
      name: 'Regulatory_Compliance',
      description: 'Regulatory_Compliance_Description',
      alt: t('Dyna_truck_booking_for_intra-city_cargo_delivery_with_insurance'),
    },
  ];

  const renderTexts = () => (
    <Box sx={{ mb: 3, textAlign: { xs: 'center', md: 'center' } }}>
      <Typography variant="h2" component="h2" sx={{ fontSize: '2.5rem !important' }}>
        {t('Transparency_Regulatory_Assurance')}
      </Typography>
    </Box>
  );

  const renderCards = () => (
    <>
      {VISIONS?.map((item, index) => (
        <m.div
          key={index}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: index * 0.2 }}
        >
          <Card
            sx={{
              bgcolor: 'background.whitePaper',
              boxShadow: 3,
              borderRadius: 2,
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <CardMedia
              sx={{ height: 350, objectFit: 'contain' }}
              image={item?.image}
              title={item?.name}
              alt={item?.alt}
            />
            <CardContent>
              <Typography gutterBottom variant="h5" component="h3">
                {t(item?.name)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t(item?.description)}
              </Typography>
            </CardContent>
          </Card>
        </m.div>
      ))}
    </>
  );

  return (
    <LazyMotion features={domAnimation}>
      <Box
        ref={ref}
        component="section"
        sx={[
          {
            overflow: 'hidden',
            py: { xs: 5, md: 7 },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Container>
          {renderTexts()}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 5,
              mt: { xs: 5, md: 7 },
            }}
          >
            {renderCards()}
          </Box>
        </Container>
      </Box>
    </LazyMotion>
  );
}
